import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MovieShowDetails, SeasonsResponse, Sources, SourceSeasons } from "../types/interfaces";
import { API_BASE_URL } from "../config";
import { describeSeasons, formatSeasons } from "../utils/seasonLabel";
import { formatPrice } from "../utils/formatPrice";
import PageHeader from "../components/PageHeader";
import { REGION_LABELS, SOURCE_TYPE_LABELS, TYPE_LABELS } from "../utils/titleType";
import SortableHeader from "../components/SortableHeader";

export default function MovieShowDetailsPage() {
    const { id } = useParams();
    const [movieDetails, setMovieDetails] = useState<MovieShowDetails | null>(null);
    const [sourcesData, setSourcesData] = useState<Sources[]>([]);
    const [sourcesDataByRegion, setSourcesDataByRegion] = useState<Sources[]>([]);
    const [sortedData, setSortedData] = useState<Sources[]>([]);
    const [sortConfig, setSortConfig] = useState<{ key: keyof Sources; direction: "asc" | "desc" } | null>(null);
    const [uniqueRegionsArray, setUniqueRegionsArray] = useState<string[]>([]);
    const [uniqueRegionsArraySorted, setUniqueRegionsArraySorted] = useState<string[]>([]);
    const [selectedValue, setSelectedValue] = useState('');
    const [seasonsData, setSeasonsData] = useState<SeasonsResponse | null>(null);
    const [seasonsLoading, setSeasonsLoading] = useState(false);

    useEffect(() => {
        if (id) {
            const fetchMovieDetails = async () => {
                const response = await fetch(`${API_BASE_URL}/api/sources/${id}`);
                const data = await response.json();
                setMovieDetails(data);
                setSourcesData(data.sources);
                // Ensure uniqueRegions is a Set<string> so we can remove duplicates
                const uniqueRegions = new Set<string>(data.sources.map((source: Sources) => source.region));
                const uniqueRegionsArray = Array.from(uniqueRegions);
                setUniqueRegionsArray(uniqueRegionsArray);
            };

            fetchMovieDetails();
        }
    }, [id]);

    // Season availability is a separate, more expensive call, so it only runs
    // for series. Movies would come back empty anyway.
    useEffect(() => {
        const isSeries = movieDetails?.type === "tv_series" || movieDetails?.type === "tv_miniseries";
        if (!id || !isSeries) {
            setSeasonsData(null);
            return;
        }

        let cancelled = false;
        const fetchSeasons = async () => {
            setSeasonsLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}/api/seasons/${id}`);
                if (!response.ok) throw new Error(`Seasons request failed: ${response.status}`);
                const data: SeasonsResponse = await response.json();
                if (!cancelled) setSeasonsData(data);
            } catch (error) {
                // The rest of the page is still useful without this, so failing
                // here just leaves the Seasons column blank.
                console.error("Error fetching season availability:", error);
                if (!cancelled) setSeasonsData(null);
            } finally {
                if (!cancelled) setSeasonsLoading(false);
            }
        };

        fetchSeasons();
        return () => {
            cancelled = true;
        };
    }, [id, movieDetails?.type]);

    useEffect(() => {
        if (uniqueRegionsArray?.includes('GB')) {
            const sortedRegions = [...uniqueRegionsArray].sort((a, b) => {
                if (a === 'GB') return -1; // 'GB' comes first
                if (b === 'GB') return 1;
                return 0;
            });
            setUniqueRegionsArraySorted(sortedRegions);
            setSelectedValue('GB');
        } else if (uniqueRegionsArray.length > 0) {
            setSelectedValue(uniqueRegionsArray[0]);
        }
    }, [uniqueRegionsArray]);

    useEffect(() => {
        const updatedData = [...sourcesData].filter(function (item) {
            return item.region === selectedValue;
        });
        setSourcesDataByRegion(updatedData);
        // handleSort(sortConfig?.key);
        setSortConfig(null);
        setSortedData(updatedData);
    }, [selectedValue, sourcesData]);

    // Season coverage is keyed by service and offering type within a region.
    // A /api/sources row can appear once per format (HD and SD), and both map to
    // the same coverage entry.
    const seasonsBySource = new Map<string, SourceSeasons>();
    if (seasonsData) {
        (seasonsData.regions[selectedValue] || []).forEach((entry) => {
            seasonsBySource.set(`${entry.source_id}|${entry.type}`, entry);
        });
    }

    const showSeasonsColumn = seasonsData !== null && seasonsData.total_seasons > 0;

    const handleSort = (key: keyof Sources) => {
        let direction: "asc" | "desc" = "asc";

        if (sortConfig?.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }

        const sorted = [...sourcesDataByRegion].sort((a, b) => {
            if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
            if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
            return 0;
        });

        setSortedData(sorted);
        setSortConfig({ key, direction });
        // console.log("Updated sortedData:", sorted);
        // console.log("Updated sortConfig:", { key, direction });
    };

    const handleRegionDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const updatedData = [...sourcesData].filter(function (item) {
            return item.region === event.target.value;
        });
        setSourcesDataByRegion(updatedData);
        setSelectedValue(event.target.value);
    };


    if (!movieDetails) {
        return (
            <div className="details-container">
                <PageHeader />
                <div className="details-loading" aria-busy="true" aria-label="Loading title">
                    <div className="skeleton skeleton-poster" />
                    <div className="skeleton skeleton-line" style={{ width: "40%" }} />
                    <div className="skeleton skeleton-line" style={{ width: "90%" }} />
                    <div className="skeleton skeleton-line" style={{ width: "75%" }} />
                </div>
            </div>
        );
    }

    return (
        <div className="details-container">
            <PageHeader />

            <div className="summary-info">
                {movieDetails?.poster && (
                    <div className="movie-show-poster">
                        <img src={movieDetails.poster} alt="" />
                    </div>
                )}
                <div className="summary-text">
                    <h1>{movieDetails?.title}</h1>
                    <div className="summary-meta">
                        <span className="badge">{TYPE_LABELS[movieDetails?.type] || movieDetails?.type}</span>
                        {movieDetails?.year ? <span>{movieDetails.year}</span> : null}
                        {seasonsData && seasonsData.total_seasons > 0 ? (
                            <span>
                                {seasonsData.total_seasons}{" "}
                                {seasonsData.total_seasons === 1 ? "season" : "seasons"}
                            </span>
                        ) : null}
                        {movieDetails?.runtime_minutes ? (
                            <span>{movieDetails.runtime_minutes} min</span>
                        ) : null}
                    </div>
                    <p className="summary-plot">{movieDetails?.plot_overview}</p>
                </div>
            </div>

            {sortedData && sortedData.length > 0 && (
                <div className="table-info">
                    <div className="table-toolbar">
                        <label htmlFor="region-dropdown">Region</label>
                        <select
                            id="region-dropdown"
                            value={selectedValue}
                            onChange={handleRegionDropdownChange}
                            className="styled-dropdown"
                        >
                            {uniqueRegionsArraySorted && uniqueRegionsArraySorted.map((item) => (
                                <option key={item} value={item}>
                                    {REGION_LABELS[item] || item}
                                </option>
                            ))}
                        </select>
                        {seasonsLoading && (
                            <span className="seasons-loading">checking season availability…</span>
                        )}
                    </div>

                    <table className="styled-table">
                        <thead>
                            <tr>
                                <SortableHeader label="Service" sortKey="name" sortConfig={sortConfig} onSort={handleSort} />
                                <SortableHeader label="Offer" sortKey="type" sortConfig={sortConfig} onSort={handleSort} />
                                {showSeasonsColumn && (
                                    <th title={`This show has ${seasonsData!.total_seasons} seasons`}>
                                        Seasons
                                    </th>
                                )}
                                <SortableHeader label="Format" sortKey="format" sortConfig={sortConfig} onSort={handleSort} />
                                <SortableHeader label="Price" sortKey="price" sortConfig={sortConfig} onSort={handleSort} />
                            </tr>
                        </thead>
                        <tbody>
                            {sortedData.map((item) => {
                                const coverage = seasonsBySource.get(`${item.source_id}|${item.type}`);
                                return (
                                <tr key={`${item.source_id}-${item.region}-${item.type}-${item.format}`}>
                                    {/* data-label drives the stacked card layout below 48rem,
                                        where the column heading travels with the value. */}
                                    <td data-label="Service" className="source-name">{item.name}</td>
                                    <td data-label="Offer">{SOURCE_TYPE_LABELS[item.type] || item.type}</td>
                                    {showSeasonsColumn && (
                                        <td
                                            data-label="Seasons"
                                            className={coverage && !coverage.complete ? "seasons-partial" : undefined}
                                            title={
                                                coverage
                                                    ? describeSeasons(
                                                          coverage.seasons,
                                                          coverage.complete,
                                                          coverage.episode_count,
                                                          seasonsData!.total_episodes
                                                      )
                                                    : "No season information for this service"
                                            }
                                        >
                                            {coverage ? formatSeasons(coverage.seasons, coverage.complete) : "—"}
                                        </td>
                                    )}
                                    <td data-label="Format">{item.format || "—"}</td>
                                    <td
                                        data-label="Price"
                                        className={!item.price ? "price-free" : undefined}
                                    >
                                        {formatPrice(item.price, item.type, item.region)}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {sourcesData && sourcesData.length <= 0 && (
                <div className="table-info">
                    <p className="no-sources">
                        <b>Not currently available on any streaming platform.</b>
                    </p>
                </div>
            )}
        </div>
    );
}